import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';

import { useAppSelector } from '../../app/hooks';
import { WallFolder } from 'types';
import { Controller, useForm } from 'react-hook-form';
import { useWeatherFolders } from './api/getWeatherFolders';
import { useCreateWeatherFolder } from './api/createWeatherFolder';
import { useFolders } from './api/getFolders';
import { useCreateFolder } from './api/createFolder';
import { useUpdateWeatherFolder } from './api/updateWeatherFolder';
import { useUpdateFolder } from './api/updateFolder';

interface Props {
  open: boolean;
  handleClose: () => void;
  selectedFolder: WallFolder | undefined;
  weather?: boolean;
}

const FolderEditDialog = ({
  open,
  handleClose,
  selectedFolder,
  weather,
}: Props) => {
  const user = useAppSelector((state) => state.auth.user);
  const { data: weatherFolders } = useWeatherFolders({ userId: user?.id });
  const { data: folders } = useFolders({ userId: user?.id });
  const createWeatherFolder = useCreateWeatherFolder({});
  const updateWeatherFolder = useUpdateWeatherFolder({});
  const createFolder = useCreateFolder({});
  const updateFolder = useUpdateFolder({});
  const { control, setValue, getValues, reset } = useForm({
    mode: 'onChange',
    defaultValues: { name: '', id: '' },
  });
  const [error, setError] = useState(false);

  const submit = async () => {
    const form = getValues();
    if (!form.name) {
      setError(true);
      return;
    }
    if (!form?.id && user) {
      if (weather && weatherFolders) {
        await createWeatherFolder.mutateAsync({
          ...form,
          position: weatherFolders.length + 1,
        });
      } else if (folders) {
        await createFolder.mutateAsync({
          ...form,
          position: folders.length + 1,
        });
      }
    } else {
      if (weather) {
        await updateWeatherFolder.mutateAsync({
          folderId: Number(form.id),
          data: form,
        });
      } else {
        await updateFolder.mutateAsync({
          data: form,
          folderId: Number(form.id),
        });
      }
    }
    reset();
    handleClose();
    setError(false);
  };

  const handleCancel = () => {
    reset();
    handleClose();
    setError(false);
  };

  useEffect(() => {
    if (selectedFolder) {
      setValue('name', selectedFolder.name);
      setValue('id', '' + selectedFolder.id);
    } else reset();
  }, [selectedFolder, open, reset, setValue]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='xs'>
      <DialogTitle>{selectedFolder ? 'Edit Folder' : 'New Folder'}</DialogTitle>
      <DialogContent>
        <Controller
          name='name'
          control={control}
          rules={{
            validate: (value) => {
              return !!value;
            },
          }}
          render={({ field: { onChange, value } }) => (
            <TextField
              id='name'
              label='Folder Name'
              name='name'
              value={value}
              onChange={onChange}
              margin='dense'
              fullWidth
              required
              error={error}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color='secondary' variant='contained'>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={() => {
            submit();
          }}
          variant='contained'
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FolderEditDialog;
